import axios from 'axios'
import {EnvHelper} from '@/helpers/EnvHelper'
import {RpcHelper} from '@/helpers/RpcHelper'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'

export type NetworkStatistics = {
  totalTransactions: number
  totalAddresses: number
  totalContracts: number
  currentWeekTransactions: number
  currentWeekAddresses: number
  currentWeekContracts: number
}

type Token = {
  hash: string
  symbol: string
  decimals: number
}

export type BalanceResponse = {
  token: Token
  amount: string
}

export type ShrikePage<T> = {
  items: T[]
  next_page_params: any
}

export type ShrikeContractItem = {
  id: number
  block_index: number
  hash: string
  contract_type: string
  name: string
}

export type ShrikeN3StatisticsResponse = {
  updated_at_ms: number
  data: {
    contracts: {
      total: number
      today: number
      yesterday: number
      this_week: number
      last_week: number
      this_month: number
      last_month: number
    }
    addresses: {
      total: number
      today: number
      yesterday: number
      this_week: number
      last_week: number
      this_month: number
      last_month: number
    }
    invocations: {
      total: number
      today: number
      yesterday: number
      this_week: number
      last_week: number
      this_month: number
      last_month: number
    }
  }
}

export class ShrikeHelper {
  static async getNetworkStatistics(): Promise<NetworkStatistics> {
    const url = `${EnvHelper.SHRIKE_URL}v1/stat/network-statistics`

    const {data} = await axios.get(url)

    return {
      totalTransactions: data.total_transactions,
      totalAddresses: data.total_addresses,
      totalContracts: data.total_contracts,
      currentWeekTransactions: data.current_week_transactions,
      currentWeekAddresses: data.current_week_addresses,
      currentWeekContracts: data.current_week_contracts,
    }
  }

  static async getN3Statistics(): Promise<ShrikeN3StatisticsResponse> {
    const url = `${EnvHelper.SHRIKE_URL}v1/stat/n3-statistics`
    const {data} = await axios.get(url)
    return data
  }

  static async getContracts(params?: {
    started_at?: string
    ended_at?: string
  }): Promise<ShrikePage<ShrikeContractItem>> {
    const url = `${EnvHelper.SHRIKE_URL}v1/contract`
    const {data} = await axios.get(url, {params})
    return data
  }

  static async getBalance(
    address: string,
    networkIdentifier?: string
  ): Promise<BalanceResponse[]> {
    const url = networkIdentifier
      ? await RpcHelper.getRpcFor(networkIdentifier)
      : await RpcHelper.getActiveRpc()

    const {data} = await axios.post(url, {
      jsonrpc: '2.0',
      method: 'getnep17balances',
      params: [address],
      id: 1,
    })

    return data.result.balance.map((item: any) => {
      return {
        token: {
          hash: item.assethash,
          symbol: item.symbol,
          decimals: item.decimals,
        },
        amount: Number(item.amount / 10 ** item.decimals),
      }
    })
  }

  /** Bridged GAS cap/usage, read from the bridge contract of its own network. */
  static async getNativeBridge() {
    const url = await BridgeNetworkHelper.n3Rpc()

    const {data} = await axios.post(url, {
      jsonrpc: '2.0',
      method: 'invokefunction',
      params: [BridgeNetworkHelper.n3BridgeScriptHash(), 'getNativeBridge'],
      id: 1,
    })

    const usedGasRaw = Number(data.result.stack[0].value[1].value) * 10 ** -8
    const totalGasRaw =
      Number(data.result.stack[0].value[4].value[4].value) * 10 ** -8

    return {
      usedGas: Math.floor(usedGasRaw),
      totalGas: Math.floor(totalGasRaw),
    }
  }
}
